import { SxProps, Theme } from '@mui/material';
import { alpha } from '@mui/material/styles';

import { getPluralForm } from '../util/helpers';
import { USA } from './countries';
import { HEADER_BASE_HEIGHT_DESKTOP, HEADER_BASE_HEIGHT_MOBILE, HOVER_MEDIA } from './ui';

// The ID prefix, not a payload flag, is what marks an option as marketplace-sourced.
export const MARKETPLACE_ID_PREFIX = 'marketplace_';

export const MARKETPLACE_TOTAL_CREDITS = 100;

// Mirrors the existing "custom" sentinel; marks the stored engagement as marketplace-sourced.
export const MARKETPLACE_ENGAGEMENT_ID = 'marketplace';

export const MARKETPLACE_DEFAULT_ALLOWED_COUNTRIES = [USA.two_digit_code];

export const MARKETPLACE_SHIPPING_COUNTRY_LABEL = 'the US';

export const MARKETPLACE_SHIPPING_COUNTRY_FULL_LABEL = 'United States';

export const MARKETPLACE_PAGE_SIZE = 24;

// Fraction of the scrollable height that, once reached, pulls the next catalog page.
export const MARKETPLACE_INFINITE_SCROLL_THRESHOLD = 0.75;

// Variant details are fetched in waves of this size, each wave settling before the next starts.
export const MARKETPLACE_DETAILS_BATCH_SIZE = 5;

export const MARKETPLACE_FILTER_DEBOUNCE_MS = 300;

// Server cap on search_query; longer input is a 400.
export const MARKETPLACE_SEARCH_MAX_LENGTH = 256;

// Opaque token from a marketplace response, echoed on later requests to skip the budget lookup.
export const MARKETPLACE_CACHE_HEADER = 'X-Marketplace-Cache';

// Filter toolbar geometry.
const FILTER_TOOLBAR_HEIGHT = 40;
export const FILTER_CHIP_HEIGHT = 32;
export const TOOLBAR_ROW_GAP = 12;
export const TOOLBAR_PADDING_Y = 10;
export const TOOLBAR_BACK_SLOT_WIDTH = 48;
export const TOOLBAR_CONTROL_GAP = 6;
export const VARIANT_CHIP_HEIGHT = 36;
export const SEARCH_FIELD_MAX_WIDTH = 480;

export const FULL_BUDGET_BODY_MAX_WIDTH = 360;

// The search keeps at least two thirds of the toolbar row; min() with the cap because a bare
// min-width would beat max-width and stretch the field past its designed size on wide rows.
export const SEARCH_FIELD_MIN_WIDTH = `min(66.6667%, ${SEARCH_FIELD_MAX_WIDTH}px)`;

// Grid track widths: narrower than the shared CARD_MIN_WIDTH, stepping down on tablet, and a
// fixed 2-up on mobile.
export const MARKETPLACE_CARD_MIN_WIDTH = 230;
export const MARKETPLACE_CARD_MIN_WIDTH_TABLET = 190;

export const MARKETPLACE_GRID_COLUMNS = {
  xs: 'repeat(2, minmax(0, 1fr))',
  md: `repeat(auto-fill, minmax(min(${MARKETPLACE_CARD_MIN_WIDTH_TABLET}px, 100%), 1fr))`,
  lg: `repeat(auto-fill, minmax(min(${MARKETPLACE_CARD_MIN_WIDTH}px, 100%), 1fr))`,
};

export const MARKETPLACE_GRID_GAP = { xs: 2, md: 2.5, lg: 3 };

export const MARKETPLACE_ENTRY_ICON_SIZE = 56;
export const MARKETPLACE_ENTRY_ICON_SIZE_MOBILE = 40;
export const EXPLORE_CARD_ICON_SIZE = 72;
export const SWAP_PIN_THUMBNAIL_SIZE = 48;
export const ALL_SET_ICON_SIZE = 56;
export const UNAVAILABLE_CARD_OPACITY = 0.5;
export const ENTRY_BANNER_MIN_KITS = 4;

// The sticky filter toolbar pins directly under the app bar.
export const STICKY_TOOLBAR_TOP_DESKTOP = HEADER_BASE_HEIGHT_DESKTOP;
export const STICKY_TOOLBAR_TOP_MOBILE = HEADER_BASE_HEIGHT_MOBILE;
export const STICKY_TOOLBAR_Z_INDEX = 15;

export const STICKY_TOOLBAR_CONDENSE_TRAVEL = TOOLBAR_PADDING_Y + FILTER_TOOLBAR_HEIGHT + TOOLBAR_ROW_GAP;

// Elevation 8 cast upward, clipped flush at the top so it never bleeds over the app bar.
export const STICKY_TOOLBAR_ELEVATION = 8;
export const STICKY_TOOLBAR_CLIP_PATH = 'inset(0 -30px -30px -30px)';

export const VENDOR_CHECK_SPINNER_SIZE = 14;

export const LOADING_OPTIONS_LABEL = 'Loading options';
export const OPTIONS_STATUS_SKELETON_WIDTH = 180;
export const OPTIONS_SUBTITLE_SKELETON_WIDTH = 280;

export const VENDOR_CHECK_DIM_OPACITY = 0.4;

export const CHECKING_BUTTON_SX: SxProps<Theme> = {
  '&.Mui-disabled': {
    opacity: VENDOR_CHECK_DIM_OPACITY,
    color: 'primary.main',
    borderColor: ({ palette }) => alpha(palette.primary.main, 0.5),
  },
};

// Matches the contained buttons, which white-labeling can colour apart from primary.main.
export const VENDOR_CHECK_SPINNER_SX: SxProps<Theme> = {
  flexShrink: 0,
  color: ({ palette }) => palette.accent.button,
  borderRadius: '50%',
  boxShadow: ({ palette }) => `inset 0 0 0 2px ${alpha(palette.accent.button, 0.25)}`,
};

export const TOOLBAR_CONTROL_SX: SxProps<Theme> = {
  display: 'flex',
  alignItems: 'center',
  gap: `${TOOLBAR_CONTROL_GAP}px`,
  height: FILTER_TOOLBAR_HEIGHT,
  boxSizing: 'border-box',
  px: 1.5,
  flexShrink: 0,
  borderRadius: 1,
  border: 1,
  borderColor: 'divider',
  backgroundColor: 'background.paper',
  color: 'text.secondary',
  fontSize: 13,
  fontWeight: 500,
  textTransform: 'none',
  whiteSpace: 'nowrap',
  transition: ({ transitions }) =>
    transitions.create(['background-color', 'border-color', 'color'], { duration: transitions.duration.shorter }),
  '& .MuiButton-startIcon': { m: 0 },
  [HOVER_MEDIA]: {
    '&:hover': { backgroundColor: 'action.hover', borderColor: 'divider' },
  },
};

// Badge geometry: smaller than MUI's default so it reads as a count on the icon.
export const WALLET_BADGE_SX: SxProps<Theme> = {
  '& .MuiBadge-badge': {
    minWidth: 16,
    height: 16,
    px: 0.5,
    fontSize: 11,
    fontWeight: 500,
    lineHeight: 1,
  },
};

export const ALL_SET_NOTICE_SX: SxProps<Theme> = {
  display: 'flex',
  alignItems: 'center',
  gap: 2.5,
  p: 3,
  borderRadius: 1,
  backgroundColor: ({ palette }) => alpha(palette.success.main, 0.05),
  border: ({ palette }) => `1px solid ${alpha(palette.success.main, 0.25)}`,
};

export const ALL_SET_ICON_SX: SxProps<Theme> = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
  width: ALL_SET_ICON_SIZE,
  height: ALL_SET_ICON_SIZE,
  borderRadius: '50%',
  backgroundColor: 'background.paper',
  boxShadow: ({ palette }) => `inset 0 0 0 1px ${alpha(palette.success.main, 0.25)}`,
};

const getCreditsLabel = (credits: number) => `${credits} ${getPluralForm(credits, 'credit', 'credits')}`;

export const BROWSE_MARKETPLACE_LABEL = 'Browse the marketplace';
export const PICKED_FOR_YOU_TITLE = 'Picked for you';
export const YOUR_PICKS_TITLE = 'Your picks';
export const getYourPicksTitle = (count: number) => getPluralForm(count, 'Your pick', YOUR_PICKS_TITLE);
export const ADD_ITEM_TO_CONTINUE_TEXT = 'Add an item to continue';
export const CONTINUE_TO_SHIPPING_LABEL = 'Continue to shipping';
export const CONTINUE_TO_OPTIONS_LABEL = 'Continue to choose options';
export const CHOOSE_OPTIONS_LABEL = 'Choose options';
export const OUT_OF_STOCK_LABEL = 'Out of stock';
export const SOLD_OUT_REMOVED_LABEL = 'Sold out — removed';
export const CURRENTLY_UNAVAILABLE_LABEL = 'Currently unavailable';
export const OVER_BUDGET_VARIANT_LABEL = 'Not available within your budget';
export const SWAP_A_PICK_LABEL = 'Swap a pick';
export const REPLACE_PICKS_LABEL = 'Replace my pick';
export const US_ADDRESSES_ONLY_LABEL = 'US addresses only';
export const LOCK_NOTE_TEXT = 'Ships from somewhere else — adding it replaces your current picks.';
export const SUPPLIER_LOCK_FALLBACK_NAME = 'this supplier';
// The supplier name sits between lead and tail in bold.
export const SUPPLIER_LOCK_BANNER_LEAD = 'Everything you pick ships from the same place — right now that’s';
export const SUPPLIER_LOCK_BANNER_TAIL = '. Adding an item that ships from somewhere else replaces your current picks.';
export const CLEAR_FILTERS_LABEL = 'Clear all filters';
export const SHOW_ALL_CATEGORIES_LABEL = 'Show all categories';
export const SHOW_ALL_ITEMS_LABEL = 'Show all items';
export const BACK_ARIA_LABEL = 'Go back';
export const BACK_TO_TOP_LABEL = 'Back to top';
export const FITS_BUDGET_TITLE = 'Everything here fits your budget';
export const MAKE_ROOM_HINT = 'Remove a pick below — it’ll be added automatically.';
export const DOES_NOT_FIT_REMAINING_TEXT = 'Doesn’t fit your remaining budget — remove a pick to make room';
export const getShipsToCountryText = (country: string = MARKETPLACE_SHIPPING_COUNTRY_FULL_LABEL) =>
  `Ships to ${country} only.`;
export const MARKETPLACE_SHIPPING_INTRO_LEAD = 'Marketplace items ship from our partners — delivery times vary.';
export const MARKETPLACE_SHIPPING_INTRO_TAIL = `Orders ship to ${MARKETPLACE_SHIPPING_COUNTRY_FULL_LABEL} only.`;
export const MARKETPLACE_SHIPPING_NOTE_LEAD = 'Marketplace items ship from our partners. Delivery times vary.';
export const NOTHING_PICKED_YET_TEXT = 'Nothing picked yet — add items from the marketplace.';
export const NOTHING_FITS_TEXT = 'Nothing in the marketplace fits the budget right now.';
export const NO_RESULTS_TEXT = 'No items match your search.';
export const FULL_BUDGET_TITLE = 'You’ve used your full budget';
export const FULL_BUDGET_BODY =
  'Nothing else fits your remaining budget. Remove a pick to make room, or continue with what you’ve chosen.';
export const NOTHING_ELSE_FITS_BODY =
  'Try another category, remove a pick to make room, or continue with what you’ve chosen.';
export const ENTRY_BANNER_TITLE = 'Prefer to pick your own item?';
export const ENTRY_BANNER_BODY = 'Skip the picks below and choose anything from the marketplace';
export const EXPLORE_TITLE = 'Explore the Marketplace';
export const EXPLORE_BODY = 'Looking for something else? Check out the marketplace for additional options.';
export const KIT_EXPLORE_TITLE = 'Build your own instead';
export const CHOOSE_YOUR_KIT_TITLE = 'Choose your kit';
export const TAKE_KIT_OR_PICK_HINT = 'Take the kit or pick your own to continue';
export const TAKING_THE_KIT_TEXT = 'You’re taking the kit';
export const DETAILS_FAILED_TOAST = 'We couldn’t load the options for this item. Please try again.';
export const CHOOSE_OPTIONS_TO_CONTINUE_TEXT = 'Choose an option for every item to continue';
export const MARKETPLACE_COUNTRY_ERROR = 'Marketplace gifts can only be delivered to a US address';
export const CHECKING_WITH_VENDOR_TEXT = 'Checking with vendor';
export const NOT_SELECTED_TEXT = 'Not selected';
export const NOTHING_TO_CHOOSE_TEXT = 'nothing to choose';
export const NO_OPTIONS_NOTICE_TITLE = 'Nothing to choose here';

export const getBudgetIntroText = (credits: number) =>
  `You have ${getCreditsLabel(credits)} to spend — each item uses a few, so mix and match as you like.`;

export const getNothingElseFitsTitle = (categoryName: string): string =>
  categoryName
    ? `Nothing else in ${categoryName} fits your remaining budget`
    : 'Nothing else matching your search fits your remaining budget';

export const getViewPicksLabel = (count: number) => `View picks (${count})`;

export const getCreditsAriaLabel = getCreditsLabel;

export const getFromCreditsAriaLabel = (credits: number) => `from ${getCreditsLabel(credits)}`;

export const getCreditsLeftOfText = (total: number) => `of ${total} credits left`;

export const getLeftoverWithFitsText = (credits: number, fitCount: number) =>
  `${getCreditsLabel(credits)} left — ${fitCount} ${getPluralForm(fitCount, 'item still fits', 'items still fit')}`;

export const getLeftoverNothingFitsText = (credits: number) =>
  `${getCreditsLabel(credits)} left — nothing else fits within them. No need to use them all.`;

export const getShipsFromNoteLead = (vendor: string) => `Ships from ${vendor}. Delivery times vary.`;

// Multi-item marketplace campaign: the brands ship independently, so the note warns that parcels
// arrive separately. One brand is named; several are counted.
export const getShipsDirectlyFromNoteLead = (brandCount: number, brandName?: string) => {
  const from = brandCount === 1 ? brandName ?? 'the brand' : `${brandCount} brands`;

  return `Ships directly from ${from}. Delivery times vary and items may arrive separately.`;
};

export const getShipsFromSupplierChipLabel = (name?: string) => `Ships from ${name || SUPPLIER_LOCK_FALLBACK_NAME}`;

// Starts with the visible label so voice control can match the button.
export const getPicksReplacedText = (supplierName?: string) =>
  supplierName ? `Picks replaced — now shipping from ${supplierName}` : 'Picks replaced';

export const getSwitchToSupplierLabel = (supplierName?: string) =>
  supplierName ? `Switch to ${supplierName}` : REPLACE_PICKS_LABEL;

export const getReplacePicksAriaLabel = (name: string) => `${REPLACE_PICKS_LABEL}: ${name}`;

export const getSupplierSwitchNoteText = (itemSupplierName?: string, lockedSupplierName?: string) =>
  `This item ships from ${itemSupplierName || 'another supplier'}. Your current picks ship from ${
    lockedSupplierName || 'a different supplier'
  } — adding it replaces them.`;

export const getCheckingAvailabilityText = (vendor?: string) => `Checking availability with ${vendor || 'the vendor'}`;

export const getAvailableText = (available: number, total: number) =>
  available === total ? `All ${total} available` : `${available} available`;

export const getNeedsYourChoiceText = (count: number) =>
  `${count} ${getPluralForm(count, 'needs', 'need')} your choice`;

export const getNoOptionsNoticeText = (count: number) =>
  `${getPluralForm(
    count,
    'This item has',
    'These items have',
  )} no options — continue to shipping whenever you’re ready`;

export const getItemCountText = (count: number) => `${count} ${getPluralForm(count, 'item', 'items')}`;
export const getItemsAddedText = (count: number) => `${getItemCountText(count)} added`;

export const getShowImageAriaLabel = (name: string) => `Show image of ${name}`;

// The sender's company name comes from init; without it the sentence falls back to a subjectless
// phrasing rather than naming the wrong company.
export const getKitSubtitleText = (credits: number, companyName?: string) =>
  `${
    companyName ? `${companyName} put` : 'We put'
  } this kit together for you. Take it as is, or build your own from the marketplace — you have ${credits} credits to spend.`;

export const getChooseKitSubtitleText = (credits: number, companyName?: string) =>
  `Take the kit ${companyName ?? 'we'} put together for you, or shop the marketplace with your ${credits} credits`;

export const getChooseKitsSubtitleText = (credits: number, companyName?: string) =>
  `Select one of the kits ${
    companyName ?? 'we'
  } put together for you, or shop the marketplace with your ${credits} credits.`;

export const getKitExploreBodyText = (credits: number) =>
  `Skip the kit and pick your own items from the marketplace — anything you like, up to ${credits} credits.`;
